import { useEffect, useState } from "react";
import { PaiementInfo } from "../../model/Model";
import { getPaiements, telechargerRecuPaiement } from "../../service/paiements/paiements.service";

function MesPaiements() {
    const [paiements, setPaiements] = useState<PaiementInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true);
                const data = await getPaiements(); 
                console.log(data);
                setPaiements(data);
            } catch (error) {
                console.error("Erreur lors du chargement des paiements:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    const handleTelechargerRecu = async (paiementId: number | undefined) => {
        if (!paiementId) {
            alert('ID de paiement invalide');
            return;
        }
        
        try {
            setDownloadingId(paiementId);
            await telechargerRecuPaiement(paiementId);
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);
            alert('Erreur lors du téléchargement du reçu. Veuillez réessayer.');
        } finally {
            setDownloadingId(null);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR');
    };

    const formatMontant = (montant: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(montant);
    };

    const getModePaiementBadge = (mode: string) => {
        const badges: { [key: string]: string } = {
            'VIREMENT': 'bg-blue-100 text-blue-800',
            'ESPECE': 'bg-green-100 text-green-800',
            'CHEQUE': 'bg-purple-100 text-purple-800'
        };
        return badges[mode] || 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <div className="pt-6 bg-gray-50 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600">Chargement des paiements...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto px-4">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 border-b border-blue-800">
                        <h2 className="text-2xl font-semibold text-white">Mes Paiements</h2>
                        <p className="text-blue-100 text-sm mt-1">Historique de tous vos paiements de loyer</p>
                    </div>

                    {paiements?.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date de paiement
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Période
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Montant
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Mode de paiement
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Description
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paiements?.map((paiement, index) => (
                                        <tr key={paiement.paiementId || index} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                        <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {formatDate(paiement.dateDePaiement)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    Du {formatDate(paiement.dateDeDebutEffet)}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    Au {formatDate(paiement.dateDeFinEffet)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-bold text-gray-900">
                                                    {formatMontant(paiement.montant)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getModePaiementBadge(paiement.modeDePaiement)}`}>
                                                    {paiement.modeDePaiement}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-700 max-w-xs truncate">
                                                    {paiement.description || <span className="text-gray-400 italic">Aucune description</span>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button
                                                    onClick={() => handleTelechargerRecu(paiement.paiementId)}
                                                    disabled={downloadingId === paiement.paiementId}
                                                    className={`inline-flex items-center px-3 py-2 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md ${
                                                        downloadingId === paiement.paiementId 
                                                            ? 'bg-gray-400 cursor-not-allowed' 
                                                            : 'bg-blue-600 hover:bg-blue-700'
                                                    }`}
                                                    title="Télécharger le reçu"
                                                >
                                                    {downloadingId === paiement.paiementId ? (
                                                        <>
                                                            <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Téléchargement...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                            Reçu
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="px-6 py-12 text-center">
                            <div className="text-gray-400 mb-4">
                                <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun paiement trouvé</h3>
                            <p className="text-gray-500">Vous n'avez effectué aucun paiement pour le moment.</p>
                        </div>
                    )}
                </div>

                {paiements?.length > 0 && (
                    <div className="mt-6 bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                    <strong>Total des paiements :</strong> {paiements.length} paiement{paiements.length > 1 ? 's' : ''}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export { MesPaiements };
